import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { addDays, addWeeks, eachDayOfInterval, format, parseISO, startOfWeek } from 'date-fns';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import CalendarOverview from '../components/CalendarOverview';
import JobScheduleBoard from '../components/JobScheduleBoard';
import useProjectsData from '../../hooks/useProjectsData';
import { DEPARTMENTS, PROJECT_JOBS, ProjectJob } from '../../data/projectsData';
import {
  TODAY,
  departmentColor,
  departmentLabel,
  formatCurrency,
  jobById,
  jobsByDepartment,
} from '../../utils/projectsUtils';

const ProjectScheduleCalendarPage: React.FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const { source, loading, error } = useProjectsData();
  const jobParam = new URLSearchParams(location.search).get('job');

  const [viewMode, setViewMode] = useState<'overview' | 'focus'>(jobParam ? 'focus' : 'overview');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(jobParam);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [activeDepts, setActiveDepts] = useState<string[]>(DEPARTMENTS);
  const [horizonStart, setHorizonStart] = useState<Date>(startOfWeek(parseISO(TODAY)));
  const [horizonWeeks, setHorizonWeeks] = useState<number>(3);

  useEffect(() => {
    if (jobParam) {
      setSelectedJobId(jobParam);
      setViewMode('focus');
    }
  }, [jobParam]);

  // When live data swaps in, re-sync the department filter to the real departments.
  useEffect(() => {
    setActiveDepts(DEPARTMENTS);
  }, [source]);

  const days = useMemo(
    () => eachDayOfInterval({ start: horizonStart, end: addDays(horizonStart, horizonWeeks * 7 - 1) }),
    [horizonStart, horizonWeeks],
  );

  const filteredJobs = useMemo(
    () =>
      PROJECT_JOBS.filter(
        (job) =>
          activeDepts.includes(job.dept_id) && (selectedJobIds.length === 0 || selectedJobIds.includes(job.job_id)),
      ),
    [activeDepts, selectedJobIds, source],
  );

  const selectJob = (jobId: string | null) => {
    setSelectedJobId(jobId);
    setViewMode(jobId ? 'focus' : 'overview');
  };

  const toggleDept = (dept: string) =>
    setActiveDepts((prev) => (prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]));

  const focusedJob: ProjectJob | undefined = selectedJobId ? jobById(selectedJobId) : undefined;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        mainTitle="Project Schedules"
        subTitle="Crew scheduling across all project departments — ServMan Tampa"
        titleIcon={<EventIcon />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load project data from ServMan. Refresh to try again.
        </Alert>
      )}
      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading project data…
        </Typography>
      )}

      {/* Controls */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_event, value) => value && setViewMode(value)}
        >
          <ToggleButton value="overview">Overview</ToggleButton>
          <ToggleButton value="focus" disabled={!selectedJobId}>
            Job Focus
          </ToggleButton>
        </ToggleButtonGroup>

        {viewMode === 'overview' && (
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 360, flexGrow: 1, maxWidth: 680 }}
            options={[...PROJECT_JOBS].sort((a, b) =>
              departmentLabel(a.dept_id).localeCompare(departmentLabel(b.dept_id)),
            )}
            groupBy={(option) => departmentLabel(option.dept_id)}
            getOptionLabel={(option) => `${option.job_id} — ${option.company || option.description}`}
            value={PROJECT_JOBS.filter((job) => selectedJobIds.includes(job.job_id))}
            onChange={(_event, values) => setSelectedJobIds(values.map((job) => job.job_id))}
            isOptionEqualToValue={(option, value) => option.job_id === value.job_id}
            limitTags={3}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Show specific jobs in the overview (optional)"
                placeholder={selectedJobIds.length ? 'Add another job…' : 'All jobs shown — pick to narrow'}
              />
            )}
          />
        )}
      </Box>

      {viewMode === 'overview' && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                Departments:
              </Typography>
              {DEPARTMENTS.map((dept) => {
                const active = activeDepts.includes(dept);
                return (
                  <Chip
                    key={dept}
                    label={`${departmentLabel(dept)} (${jobsByDepartment(dept).length})`}
                    size="small"
                    onClick={() => toggleDept(dept)}
                    variant={active ? 'filled' : 'outlined'}
                    sx={{
                      fontWeight: 600,
                      backgroundColor: active ? departmentColor(dept) : 'transparent',
                      color: active ? 'white' : departmentColor(dept),
                      borderColor: departmentColor(dept),
                    }}
                  />
                );
              })}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton size="small" onClick={() => setHorizonStart(addWeeks(horizonStart, -1))}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 170, textAlign: 'center' }}>
                {format(days[0], 'MMM d')} – {format(days[days.length - 1], 'MMM d, yyyy')}
              </Typography>
              <IconButton size="small" onClick={() => setHorizonStart(addWeeks(horizonStart, 1))}>
                <ChevronRightIcon />
              </IconButton>
              <Button size="small" onClick={() => setHorizonStart(startOfWeek(parseISO(TODAY)))}>
                Today
              </Button>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={horizonWeeks}
                onChange={(_event, value) => value && setHorizonWeeks(value)}
              >
                <ToggleButton value={2}>2 wk</ToggleButton>
                <ToggleButton value={3}>3 wk</ToggleButton>
                <ToggleButton value={4}>4 wk</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <CalendarOverview jobs={filteredJobs} days={days} onSelectJob={selectJob} />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Each cell shows the number of technicians scheduled on that job that day. Click any job row to open its
            drag-and-drop crew board.
          </Typography>
        </>
      )}

      {viewMode === 'focus' && focusedJob && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => selectJob(null)}>
                Overview
              </Button>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">{focusedJob.company || focusedJob.description}</Typography>
                  <Chip
                    size="small"
                    label={departmentLabel(focusedJob.dept_id)}
                    sx={{ backgroundColor: departmentColor(focusedJob.dept_id), color: 'white', fontWeight: 600 }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {focusedJob.job_id} · {focusedJob.description} · {formatCurrency(focusedJob.contract_amount)}
                </Typography>
              </Box>
            </Box>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              onClick={() => history.push(`/projects/jobs/${focusedJob.job_id}`)}
            >
              Open Job Detail
            </Button>
          </Box>

          <JobScheduleBoard jobId={focusedJob.job_id} />
        </>
      )}
    </Box>
  );
};

export default ProjectScheduleCalendarPage;
